import React, { useState, useContext, useEffect } from "react";

import {
  Button,
  CssBaseline,
  TextField,
  Typography,
  InputAdornment,
  IconButton,
  CircularProgress,
} from "@material-ui/core";

import {
  Visibility,
  VisibilityOff,
  MailOutline,
  LockOutlined,
  Forum,
  Group,
  ViewColumn,
  FlashOn,
  Done,
  DoneAll,
} from "@material-ui/icons";

import { makeStyles, useTheme } from "@material-ui/core/styles";

import { i18n } from "../../translate/i18n";

import { AuthContext } from "../../context/Auth/AuthContext";
import logoBlack from "../../assets/ticketfacil-logo-black.png";
import logoWhite from "../../assets/ticketfacil-logo-white.png";

// Conversa de demonstração exibida no painel lateral (repete em loop)
const DEMO_CHAT = [
  { from: "client", text: "Oi! Meu pedido ainda não chegou 😕" },
  { from: "agent", text: "Olá! Sou a Ana, vou verificar pra você agora mesmo." },
  { from: "client", text: "Obrigado! É o pedido 4821." },
  { from: "agent", text: "Prontinho: ele sai para entrega hoje até 18h 🚚" },
  { from: "client", text: "Perfeito, atendimento nota 10! 💜" },
];

const FEATURES = [
  { icon: Group, label: "Vários atendentes, um só número" },
  { icon: ViewColumn, label: "Filas, tags e Kanban" },
  { icon: FlashOn, label: "Respostas rápidas e agendamentos" },
];

const useStyles = makeStyles((theme) => {
  const dark = theme.palette.type === "dark";
  return {
    "@keyframes floatBlob": {
      "0%, 100%": { transform: "translate(0, 0) scale(1)" },
      "50%": { transform: "translate(30px, -40px) scale(1.12)" },
    },
    "@keyframes bubbleIn": {
      from: { opacity: 0, transform: "translateY(14px) scale(.96)" },
      to: { opacity: 1, transform: "translateY(0) scale(1)" },
    },
    "@keyframes typing": {
      "0%, 60%, 100%": { transform: "translateY(0)", opacity: 0.5 },
      "30%": { transform: "translateY(-4px)", opacity: 1 },
    },
    "@keyframes fadeUp": {
      from: { opacity: 0, transform: "translateY(20px)" },
      to: { opacity: 1, transform: "translateY(0)" },
    },
    "@keyframes floaty": {
      "0%, 100%": { transform: "translateY(0)" },
      "50%": { transform: "translateY(-8px)" },
    },
    "@keyframes pulse": {
      "0%": { boxShadow: "0 0 0 0 rgba(74, 222, 128, .7)" },
      "70%": { boxShadow: "0 0 0 8px rgba(74, 222, 128, 0)" },
      "100%": { boxShadow: "0 0 0 0 rgba(74, 222, 128, 0)" },
    },
    root: {
      minHeight: "100vh",
      display: "flex",
      background: dark ? "#0f0b1a" : "#f6f4fb",
    },

    // ---------- painel ilustrativo ----------
    hero: {
      position: "relative",
      flex: "1 1 55%",
      overflow: "hidden",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      padding: theme.spacing(6, 8),
      color: "#fff",
      background:
        "linear-gradient(135deg, #2E1065 0%, #5B21B6 45%, #7C3AED 100%)",
      [theme.breakpoints.down("sm")]: { display: "none" },
    },
    blob: {
      position: "absolute",
      borderRadius: "50%",
      filter: "blur(60px)",
      opacity: 0.45,
      animation: "$floatBlob 14s ease-in-out infinite",
      pointerEvents: "none",
    },
    heroContent: {
      position: "relative",
      zIndex: 1,
      maxWidth: 560,
      margin: "0 auto",
      width: "100%",
      animation: "$fadeUp .8s ease both",
    },
    heroLogo: { height: 44, marginBottom: theme.spacing(4) },
    heroTitle: {
      fontSize: 38,
      fontWeight: 800,
      lineHeight: 1.15,
      letterSpacing: -0.5,
      marginBottom: theme.spacing(1.5),
      "& span": {
        background: "linear-gradient(90deg, #C4B5FD, #F0ABFC)",
        WebkitBackgroundClip: "text",
        WebkitTextFillColor: "transparent",
      },
    },
    heroSubtitle: {
      fontSize: 16,
      opacity: 0.85,
      marginBottom: theme.spacing(4),
      maxWidth: 460,
    },

    phone: {
      position: "relative",
      width: "100%",
      maxWidth: 400,
      borderRadius: 24,
      background: "rgba(255,255,255,.10)",
      border: "1px solid rgba(255,255,255,.18)",
      backdropFilter: "blur(14px)",
      boxShadow: "0 30px 60px -20px rgba(0,0,0,.45)",
      overflow: "hidden",
      animation: "$floaty 6s ease-in-out infinite",
    },
    phoneHeader: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: "12px 16px",
      background: "rgba(255,255,255,.08)",
      borderBottom: "1px solid rgba(255,255,255,.12)",
    },
    avatar: {
      width: 34,
      height: 34,
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontWeight: 700,
      background: "linear-gradient(135deg, #F0ABFC, #A78BFA)",
      color: "#2E1065",
    },
    online: {
      display: "inline-block",
      width: 8,
      height: 8,
      borderRadius: "50%",
      background: "#4ADE80",
      marginRight: 6,
      animation: "$pulse 2s infinite",
    },
    chatBody: {
      height: 330,
      padding: 16,
      display: "flex",
      flexDirection: "column",
      justifyContent: "flex-end",
      gap: 8,
      overflow: "hidden",
    },
    bubble: {
      maxWidth: "80%",
      padding: "8px 12px",
      borderRadius: 14,
      fontSize: 14,
      lineHeight: 1.35,
      animation: "$bubbleIn .45s cubic-bezier(.2,.8,.2,1) both",
      boxShadow: "0 4px 14px -6px rgba(0,0,0,.35)",
    },
    client: {
      alignSelf: "flex-start",
      background: "#fff",
      color: "#1f1235",
      borderBottomLeftRadius: 4,
    },
    agent: {
      alignSelf: "flex-end",
      background: "linear-gradient(135deg, #A78BFA, #7C3AED)",
      color: "#fff",
      borderBottomRightRadius: 4,
    },
    meta: {
      display: "flex",
      justifyContent: "flex-end",
      alignItems: "center",
      gap: 2,
      fontSize: 10,
      opacity: 0.75,
      marginTop: 2,
      "& svg": { fontSize: 13 },
    },
    typingBubble: {
      alignSelf: "flex-start",
      display: "flex",
      gap: 4,
      padding: "10px 14px",
      borderRadius: 14,
      borderBottomLeftRadius: 4,
      background: "#fff",
      animation: "$bubbleIn .3s ease both",
      "& i": {
        width: 7,
        height: 7,
        borderRadius: "50%",
        background: "#7C3AED",
        animation: "$typing 1.2s infinite",
      },
      "& i:nth-child(2)": { animationDelay: ".15s" },
      "& i:nth-child(3)": { animationDelay: ".3s" },
    },
    features: {
      display: "flex",
      flexWrap: "wrap",
      gap: 10,
      marginTop: theme.spacing(4),
    },
    feature: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      padding: "8px 14px",
      borderRadius: 999,
      fontSize: 13,
      background: "rgba(255,255,255,.12)",
      border: "1px solid rgba(255,255,255,.18)",
      animation: "$fadeUp .7s ease both",
      "& svg": { fontSize: 18, color: "#DDD6FE" },
    },

    // ---------- formulário ----------
    formSide: {
      position: "relative",
      flex: "1 1 45%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: theme.spacing(4, 2),
      overflow: "hidden",
    },
    mobileBlob: {
      [theme.breakpoints.up("md")]: { display: "none" },
    },
    card: {
      position: "relative",
      zIndex: 1,
      width: "100%",
      maxWidth: 420,
      padding: theme.spacing(5, 4),
      borderRadius: 24,
      background: dark ? "rgba(30, 22, 48, .85)" : "rgba(255,255,255,.9)",
      backdropFilter: "blur(10px)",
      boxShadow: dark
        ? "0 20px 50px -20px rgba(0,0,0,.8)"
        : "0 20px 50px -20px rgba(91, 33, 182, .35)",
      border: dark ? "1px solid rgba(167,139,250,.15)" : "1px solid #ede9fe",
      animation: "$fadeUp .7s ease both",
      [theme.breakpoints.down("xs")]: { padding: theme.spacing(4, 2.5) },
    },
    cardLogo: {
      display: "block",
      height: 48,
      margin: "0 auto",
      marginBottom: theme.spacing(3),
      maxWidth: "100%",
      objectFit: "contain",
    },
    welcomeIcon: {
      width: 52,
      height: 52,
      borderRadius: 16,
      margin: "0 auto 12px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "#fff",
      background: "linear-gradient(135deg, #7C3AED, #5B21B6)",
      boxShadow: "0 10px 20px -8px rgba(124,58,237,.7)",
      animation: "$floaty 4s ease-in-out infinite",
    },
    title: { fontWeight: 800, textAlign: "center" },
    subtitle: {
      textAlign: "center",
      color: theme.palette.text.secondary,
      marginBottom: theme.spacing(2),
    },
    field: {
      "& .MuiOutlinedInput-root": {
        borderRadius: 14,
        transition: "box-shadow .2s",
        "&.Mui-focused": {
          boxShadow: "0 0 0 4px rgba(124, 58, 237, .15)",
        },
      },
    },
    adornIcon: { color: theme.palette.text.secondary },
    submit: {
      marginTop: theme.spacing(3),
      height: 50,
      borderRadius: 14,
      fontWeight: 700,
      fontSize: 15,
      textTransform: "none",
      background: "linear-gradient(135deg, #7C3AED 0%, #5B21B6 100%)",
      boxShadow: "0 12px 24px -10px rgba(124, 58, 237, .8)",
      transition: "transform .15s, box-shadow .15s",
      "&:hover": {
        transform: "translateY(-1px)",
        boxShadow: "0 16px 28px -10px rgba(124, 58, 237, .9)",
        background: "linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)",
      },
    },
    footer: {
      textAlign: "center",
      marginTop: theme.spacing(3),
      fontSize: 12,
      color: theme.palette.text.secondary,
      lineHeight: 1.6,
    },
    footerLink: {
      color: theme.palette.primary.main,
      fontWeight: 600,
      textDecoration: "none",
      "&:hover": { textDecoration: "underline" },
    },
    "@media (prefers-reduced-motion: reduce)": {
      blob: { animation: "none" },
      phone: { animation: "none" },
      welcomeIcon: { animation: "none" },
    },
  };
});

// Anima a conversa: mostra "digitando..." antes de cada mensagem e recomeça no fim
const useDemoChat = () => {
  const [count, setCount] = useState(0);
  const [typing, setTyping] = useState(false);

  useEffect(() => {
    let timer;
    if (count >= DEMO_CHAT.length) {
      timer = setTimeout(() => setCount(0), 4000);
    } else {
      setTyping(true);
      timer = setTimeout(() => {
        setTyping(false);
        setCount((c) => c + 1);
      }, count === 0 ? 900 : 1700);
    }
    return () => clearTimeout(timer);
  }, [count]);

  const next = DEMO_CHAT[count];
  return {
    messages: DEMO_CHAT.slice(0, count),
    typingFrom: typing && next ? next.from : null,
  };
};

const Login = () => {
  const classes = useStyles();
  const theme = useTheme();

  const [user, setUser] = useState({ email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const { handleLogin } = useContext(AuthContext);
  const { messages, typingFrom } = useDemoChat();

  const handleChangeInput = (e) => {
    setUser({ ...user, [e.target.name]: e.target.value });
  };

  const handlSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await handleLogin(user);
    } finally {
      setSubmitting(false);
    }
  };

  const logo = theme.palette.type === "dark" ? logoWhite : logoBlack;
  const now = new Date().toLocaleTimeString("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
  });

  return (
    <div className={classes.root}>
      <CssBaseline />

      <section className={classes.hero} aria-hidden="true">
        <span
          className={classes.blob}
          style={{ width: 380, height: 380, background: "#C084FC", top: -80, left: -60 }}
        />
        <span
          className={classes.blob}
          style={{ width: 320, height: 320, background: "#F0ABFC", bottom: -60, right: -40, animationDelay: "-5s" }}
        />
        <span
          className={classes.blob}
          style={{ width: 240, height: 240, background: "#818CF8", top: "40%", right: "30%", animationDelay: "-9s" }}
        />

        <div className={classes.heroContent}>
          <img src={logoWhite} alt="" className={classes.heroLogo} />
          <div className={classes.heroTitle}>
            Atendimento que <span>encanta</span>
            <br />
            seus clientes.
          </div>
          <div className={classes.heroSubtitle}>
            Centralize as conversas do WhatsApp, distribua entre a equipe e
            responda mais rápido — tudo em um só lugar.
          </div>

          <div className={classes.phone}>
            <div className={classes.phoneHeader}>
              <div className={classes.avatar}>C</div>
              <div>
                <div style={{ fontWeight: 600, fontSize: 14 }}>Cliente</div>
                <div style={{ fontSize: 11, opacity: 0.8 }}>
                  <span className={classes.online} />
                  {typingFrom === "client" ? "digitando..." : "online"}
                </div>
              </div>
              <Forum style={{ marginLeft: "auto", opacity: 0.8 }} />
            </div>
            <div className={classes.chatBody}>
              {messages.map((m, i) => (
                <div key={i} className={`${classes.bubble} ${classes[m.from]}`}>
                  {m.text}
                  <div className={classes.meta}>
                    {now}
                    {m.from === "agent" &&
                      (i < messages.length - 1 ? <DoneAll /> : <Done />)}
                  </div>
                </div>
              ))}
              {typingFrom === "client" && (
                <div className={classes.typingBubble}>
                  <i />
                  <i />
                  <i />
                </div>
              )}
            </div>
          </div>

          <div className={classes.features}>
            {FEATURES.map(({ icon: Icon, label }, i) => (
              <div
                key={label}
                className={classes.feature}
                style={{ animationDelay: `${0.3 + i * 0.15}s` }}
              >
                <Icon />
                {label}
              </div>
            ))}
          </div>
        </div>
      </section>

      <main className={classes.formSide}>
        <span
          className={`${classes.blob} ${classes.mobileBlob}`}
          style={{ width: 280, height: 280, background: "#A78BFA", top: -80, right: -80 }}
        />
        <span
          className={`${classes.blob} ${classes.mobileBlob}`}
          style={{ width: 240, height: 240, background: "#F0ABFC", bottom: -60, left: -60, animationDelay: "-6s" }}
        />

        <div className={classes.card}>
          <img src={logo} alt="TicketFacil" className={classes.cardLogo} />
          <div className={classes.welcomeIcon}>
            <Forum />
          </div>
          <Typography component="h1" variant="h5" className={classes.title}>
            Bem-vindo de volta!
          </Typography>
          <Typography variant="body2" className={classes.subtitle}>
            Entre para continuar seus atendimentos
          </Typography>

          <form noValidate onSubmit={handlSubmit}>
            <TextField
              variant="outlined"
              margin="normal"
              required
              fullWidth
              id="email"
              label={i18n.t("login.form.email")}
              name="email"
              value={user.email}
              onChange={handleChangeInput}
              autoComplete="email"
              autoFocus
              className={classes.field}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <MailOutline className={classes.adornIcon} />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              variant="outlined"
              margin="normal"
              required
              fullWidth
              name="password"
              label={i18n.t("login.form.password")}
              id="password"
              value={user.password}
              onChange={handleChangeInput}
              autoComplete="current-password"
              type={showPassword ? "text" : "password"}
              className={classes.field}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlined className={classes.adornIcon} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword((e) => !e)}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              disabled={submitting}
              className={classes.submit}
            >
              {submitting ? (
                <CircularProgress size={24} style={{ color: "#fff" }} />
              ) : (
                i18n.t("login.buttons.submit")
              )}
            </Button>
            {/* Cadastro público desligado no TicketFacil: usuários são criados pelo admin */}
          </form>

          <div className={classes.footer}>
            <div>
              Desenvolvido por <strong>BMG Tech</strong>
            </div>
            <div>
              Suporte:{" "}
              <a href="mailto:suporte@bmgtech.com.br" className={classes.footerLink}>
                suporte@bmgtech.com.br
              </a>
            </div>
            <div style={{ marginTop: 4, opacity: 0.8 }}>
              © {new Date().getFullYear()} TicketFacil
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Login;
